import superjson from 'superjson'

import type {
  AppRouter,
  AppRouterInputs,
  AppRouterOutputs,
} from '@helpscout/trpc/src/routers/_app'
import { defaultContext } from '@tanstack/react-query'
import { httpLink as clientHttpLink, createTRPCProxyClient } from '@trpc/client'
import { createTRPCReact, getQueryKey, httpLink } from '@trpc/react-query'

import type { HsGlobal } from '../types/global'
import { isTestEnv } from './env'

export { getQueryKey }
export { AppRouterInputs, AppRouterOutputs }

/**
 * TRPC React Query Instance
 * Use it to call trpc methods from React components.
 */
export const trpc: ReturnType<typeof createTRPCReact<AppRouter>> =
  createTRPCReact<AppRouter>({
    reactQueryContext: defaultContext,
  })

/**
 * TRPC React Query Provider
 * Use it to wrap your app in a TRPC React Query Provider.
 */
// Explicit type so the .d.ts names this via `trpc` (portable) instead of
// inferring nested @helpscout/* client types that trip TS2742 on a clean install.
export const TrpcProvider: typeof trpc.Provider = trpc.Provider

/**
 * TRPC Client
 * Use it to create a TRPC client to pass to the TrpcProvider.
 */
// Explicit return type so the .d.ts names this via `trpc` (portable) instead of
// inferring nested @helpscout/* client types that trip TS2742 on a clean install.
export const createTrpcClient = (): ReturnType<typeof trpc.createClient> => {
  const hsGlobal = (window as unknown as { hsGlobal?: HsGlobal }).hsGlobal
  const {
    hsAppUiServerUrl = '',
    trpcToken = '',
    correlationId = '',
  } = hsGlobal ?? {}

  // hsGlobal is always injected in hs-app-ui; its absence means a standalone
  // host (e.g. the customer-portal SPA), not a misconfiguration to flag.
  if (hsGlobal && (!hsAppUiServerUrl || !trpcToken)) {
    !isTestEnv() && console.error('hsAppUiServerUrl or trpcToken not set')
  }

  const urlHasTrailingSlash = hsAppUiServerUrl?.endsWith('/')
  const url = `${hsAppUiServerUrl}${urlHasTrailingSlash ? '' : '/'}trpc`

  return trpc.createClient({
    links: [
      httpLink({
        url,
        headers() {
          return {
            'X-CSRF-Token':
              (window as unknown as { HS?: { csrfToken?: string } })?.HS
                ?.csrfToken ?? '',
            'correlation-id': correlationId ?? '',
            Authorization: `Bearer ${trpcToken}`,
          }
        },
      }),
    ],
    transformer: superjson,
  })
}

/**
 * TRPC Standalone Client
 * Use it to create a TRPC client to use standalone without React
 */
export const createStandaloneTrpcClient = () => {
  const hsGlobal = (window as any).hsGlobal
  const { hsAppUiServerUrl = '', trpcToken = '' } = hsGlobal ?? {}

  // hsGlobal is always injected in hs-app-ui; its absence means a standalone
  // host (e.g. the customer-portal SPA), not a misconfiguration to flag.
  if (hsGlobal && (!hsAppUiServerUrl || !trpcToken)) {
    !isTestEnv() && console.error('hsAppUiServerUrl or trpcToken not set')
  }

  const urlHasTrailingSlash = hsAppUiServerUrl?.endsWith('/')
  const url = `${hsAppUiServerUrl}${urlHasTrailingSlash ? '' : '/'}trpc`

  return createTRPCProxyClient<AppRouter>({
    links: [
      clientHttpLink({
        url,
        headers() {
          return {
            Authorization: `Bearer ${trpcToken}`,
          }
        },
      }),
    ],
    transformer: superjson,
  })
}

// Explicit type (same reason as TrpcProvider) so the .d.ts stays portable on a
// clean install instead of inferring nested @helpscout/* client types (TS2742).
export const standaloneTrpc: ReturnType<typeof createStandaloneTrpcClient> =
  createStandaloneTrpcClient()
